import { VISIME_PUBLIC_ORIGIN, defaultOgImageUrl } from './seoSite';

export type HreflangLink = { hreflang: string; href: string };

export function normalizeSeoPath(path: string): string {
  if (!path || path === '/') return '/';
  const withoutQuery = path.split('?')[0]?.split('#')[0] ?? path;
  return withoutQuery.startsWith('/') ? withoutQuery : `/${withoutQuery}`;
}

export function absolutePublicUrl(path: string, lang?: 'en' | 'de'): string {
  const base = VISIME_PUBLIC_ORIGIN.replace(/\/$/, '');
  const normalized = normalizeSeoPath(path);
  const url = new URL(normalized, `${base}/`);
  if (lang) {
    url.searchParams.set('lang', lang);
  }
  return url.toString();
}

export function hreflangAlternates(path: string): HreflangLink[] {
  const normalized = normalizeSeoPath(path);
  return [
    { hreflang: 'en', href: absolutePublicUrl(normalized, 'en') },
    { hreflang: 'de', href: absolutePublicUrl(normalized, 'de') },
    { hreflang: 'x-default', href: absolutePublicUrl(normalized, 'en') },
  ];
}

export type SeoTagSnapshot = {
  title: string;
  metas: Map<string, string>;
  links: Map<string, string>;
  htmlLang: string;
  jsonLdIds: string[];
};

function metaKey(name: string, attr: 'name' | 'property' = 'name'): string {
  return `${attr}:${name}`;
}

function linkKey(rel: string, hreflang?: string | null): string {
  return hreflang ? `link:${rel}:${hreflang}` : `link:${rel}`;
}

export function captureSeoSnapshot(): SeoTagSnapshot {
  const metas = new Map<string, string>();
  document.querySelectorAll('meta[name], meta[property]').forEach((el) => {
    const name = el.getAttribute('name') ?? el.getAttribute('property');
    const content = el.getAttribute('content');
    if (!name || content == null) return;
    const attr = el.hasAttribute('property') ? 'property' : 'name';
    metas.set(metaKey(name, attr), content);
  });
  const links = new Map<string, string>();
  document.querySelectorAll('link[rel]').forEach((el) => {
    const rel = el.getAttribute('rel');
    const href = el.getAttribute('href');
    if (!rel || !href) return;
    links.set(linkKey(rel, el.getAttribute('hreflang')), href);
  });
  const jsonLdIds: string[] = [];
  document.querySelectorAll('script[type="application/ld+json"][data-visime-seo]').forEach((el) => {
    const id = el.getAttribute('data-visime-seo');
    if (id) jsonLdIds.push(id);
  });
  return {
    title: document.title,
    metas,
    links,
    htmlLang: document.documentElement.lang || 'en',
    jsonLdIds,
  };
}

function setMeta(name: string, content: string, attr: 'name' | 'property' = 'name'): void {
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setLink(rel: string, href: string, hreflang?: string): void {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`;
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    if (hreflang) el.setAttribute('hreflang', hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function removeManagedHreflang(): void {
  document.querySelectorAll('link[rel="alternate"][data-visime-seo-hreflang]').forEach((el) => {
    el.remove();
  });
}

function applyHreflang(path: string): void {
  removeManagedHreflang();
  hreflangAlternates(path).forEach(({ hreflang, href }) => {
    const el = document.createElement('link');
    el.setAttribute('rel', 'alternate');
    el.setAttribute('hreflang', hreflang);
    el.setAttribute('href', href);
    el.setAttribute('data-visime-seo-hreflang', '1');
    document.head.appendChild(el);
  });
}

function removeJsonLdManaged(ids: string[]): void {
  ids.forEach((id) => {
    document.querySelector(`script[type="application/ld+json"][data-visime-seo="${id}"]`)?.remove();
  });
}

function applyJsonLd(blocks: Record<string, unknown> | Record<string, unknown>[] | undefined, slot: string): string[] {
  if (!blocks) return [];
  const list = Array.isArray(blocks) ? blocks : [blocks];
  const ids: string[] = [];
  list.forEach((block, index) => {
    const id = `${slot}-${index}`;
    ids.push(id);
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.setAttribute('data-visime-seo', id);
    el.textContent = JSON.stringify(block);
    document.head.appendChild(el);
  });
  return ids;
}

export type ApplyPageSeoInput = {
  title: string;
  description?: string;
  path: string;
  imageUrl?: string;
  robots?: string;
  hreflang?: boolean;
  htmlLang?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  jsonLdSlot?: string;
};

export function applyPageSeoToDocument(input: ApplyPageSeoInput): { jsonLdIds: string[] } {
  const canonical = absolutePublicUrl(input.path);
  const image = input.imageUrl ?? defaultOgImageUrl();
  const description = input.description ?? '';

  document.title = input.title;
  if (input.htmlLang) {
    document.documentElement.lang = input.htmlLang;
  }

  setMeta('description', description);
  setMeta('robots', input.robots ?? 'index, follow');

  setLink('canonical', canonical);
  if (input.hreflang) {
    applyHreflang(input.path);
  } else {
    removeManagedHreflang();
  }

  setMeta('og:title', input.title, 'property');
  setMeta('og:description', description, 'property');
  setMeta('og:url', canonical, 'property');
  setMeta('og:type', 'website', 'property');
  setMeta('og:site_name', 'VisiMe', 'property');
  setMeta('og:image', image, 'property');
  setMeta('og:locale', input.htmlLang === 'de' ? 'de_CH' : 'en_US', 'property');

  setMeta('twitter:card', 'summary_large_image');
  setMeta('twitter:title', input.title);
  setMeta('twitter:description', description);
  setMeta('twitter:image', image);

  const jsonLdIds = applyJsonLd(input.jsonLd, input.jsonLdSlot ?? 'page');

  return { jsonLdIds };
}

export function restoreSeoSnapshot(snapshot: SeoTagSnapshot, managedJsonLdIds: string[]): void {
  document.title = snapshot.title;
  document.documentElement.lang = snapshot.htmlLang;

  document.querySelectorAll('link[data-visime-seo-hreflang]').forEach((el) => el.remove());
  removeJsonLdManaged(managedJsonLdIds);

  snapshot.metas.forEach((content, key) => {
    const [kind, name] = key.split(':');
    if (kind === 'name' || kind === 'property') {
      setMeta(name, content, kind);
    }
  });

  snapshot.links.forEach((href, key) => {
    const parts = key.split(':');
    if (parts[0] !== 'link') return;
    const rel = parts[1];
    const hreflang = parts[2];
    if (rel) setLink(rel, href, hreflang);
  });
}

export function buildFaqPageJsonLd(items: { question: string; answer: string }[], pageUrl: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
    url: pageUrl,
  };
}

export function buildWebPageJsonLd(title: string, description: string, path: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: title,
    description,
    url: absolutePublicUrl(path),
    isPartOf: {
      '@type': 'WebSite',
      name: 'VisiMe',
      url: VISIME_PUBLIC_ORIGIN,
    },
  };
}
